import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { deferred } from '@sas/nova-core/util';
import { getKeyLabel, importShortcuts, PLATFORMS } from '..';
import { FileImporter, ShortcutsInput } from '../__internal__';
import { useParentSize } from '../../../hooks/useParentSize';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { getOSName } from '../../../util';
import { download } from '../../../util/fileUtil';
import { Dialog } from '../../Dialog';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { GroupHeaderItem, List } from '../../List';
import { useListState } from '../../List/useListState';
import { MessageDialog, useMessageDialogText } from '../../MessageDialog';
import { Toolbar } from '../../Toolbar';
import reservedShortcuts from '../data/reserved.json';
import bundle from '../NovaComponents-KeyboardShortcuts-gui-icu.properties';
import cn from './ManageDialog.css.json';
import './ManageDialog.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const SHORTCUT_COUNT = 2;
// The action name ends with period, should not append colon
const NO_COLON_NAME = ['gotoPrimaryTab', 'gotoSecondaryTab'];
export const ManageDialog = props => {
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const [setRef, dimensions] = useParentSize();
  const {
    CONTINUE,
    CANCEL,
    CLOSE
  } = useMessageDialogText();
  const [messageDialogProps, setMessageDialogProps] = useState();
  const listState = useListState();
  const [searchValue, setSearchValue] = useState('');
  const [listData, setListData] = useState([]);
  const fileExt = 'settings';
  const fileName = `${localize('shortcut.filename.label')}.${fileExt}`;
  const [platform, setPlatform] = useState(getOSName().toLowerCase());
  const platforms = [{
    // Intentionally don't localize operating system names
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    text: 'Microsoft Windows',
    key: 'win'
  }, {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    text: localize('and.label', {
      '0': 'Apple OS X',
      '1': 'macOS'
    }),
    key: 'mac'
  }, {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    text: 'Linux',
    key: 'linux'
  }];
  const {
    filteredListData,
    matchCount
  } = filterListData(listData, localize, searchValue, platform);
  const fileImporterRef = useRef(null);
  const onImport = userDefined => {
    const shortcuts = saveListData(props.shortcuts, listData);
    importShortcuts(shortcuts, userDefined);
    setListData(getListData(shortcuts, localize));
  };
  useEffect(() => {
    if (props.isOpen) {
      setListData(getListData(props.shortcuts, localize));
    }
  }, [localize, props.isOpen, props.shortcuts]);
  useEffect(() => {
    // clear search value after close
    if (!props.isOpen) setSearchValue('');
  }, [props.isOpen]);
  const validate = () => {
    const d = deferred();
    const invalidActions = listData.filter(action => {
      const defaults = action.default ? action.default[platform] : null,
        keys = action[platform] ?? defaults ?? [];
      return action.required && keys.findIndex(value => value) === -1;
    });
    if (invalidActions.length > 0) {
      setMessageDialogProps({
        type: 'warning',
        isOpen: true,
        title: localize('manageDialog.dialog.title'),
        text: localize('notValidContinue.msg'),
        firstAction: {
          text: CONTINUE,
          onPress: () => {
            invalidActions.forEach(action => action[platform] = undefined);
            setListData(listData.slice());
            setMessageDialogProps(prev => ({
              ...prev,
              isOpen: false
            }));
            d.resolve();
          }
        },
        dismissAction: {
          text: CANCEL,
          onPress: () => {
            setMessageDialogProps(prev => ({
              ...prev,
              isOpen: false
            }));
            d.reject();
          }
        }
      });
    } else d.resolve();
    return d.promise;
  };
  const showError = text => {
    setMessageDialogProps({
      type: 'error',
      isOpen: true,
      title: localize('editShortcut.dialog.title'),
      text,
      dismissAction: {
        text: CLOSE,
        onPress: () => setMessageDialogProps(prev => ({
          ...prev,
          isOpen: false
        }))
      }
    });
  };
  const tryGrab = (key, actionIndex, keyIndex) => {
    for (const i in reservedShortcuts) {
      if (reservedShortcuts.hasOwnProperty(i)) {
        const action = reservedShortcuts[i];
        const keys = action.default[platform];
        for (let j = 0; j < keys.length; j++) {
          if (keys[j] === key) {
            showError(localize('cannotReassign.msg', {
              key: getKeyLabel(key, localize),
              action: localize(action.description)
            }));
            return false;
          }
        }
      }
    }
    for (let i = 0; i < listData.length; i++) {
      const action = listData[i];
      const defaults = action.default[platform];
      const keys = action[platform] ?? defaults ?? [];
      for (let k = 0; k < SHORTCUT_COUNT; k++) {
        const currentKey = keys[k];
        if ((i !== actionIndex || k !== keyIndex) && currentKey === key) {
          // already assigned to a different action
          if (k === 0 && action.editable === false) {
            showError(localize('cannotReassign.msg', {
              key: getKeyLabel(key, localize),
              action: action.description
            }));
            return false;
          }
          if (action.required &&
          // if it's the only shortcut for a required action
          keys.reduce((count, value) => value ? count + 1 : count, 0) === 1) {
            showError(localize('cannotReassignAlternateShortcut.msg', {
              key: getKeyLabel(key, localize),
              // See comment in message bundle for why action/description is duplicated.
              action: action.description,
              sameAsAction: action.description
            }));
            return false;
          }
          const newAction = listData[actionIndex];
          setMessageDialogProps({
            type: 'warning',
            isOpen: true,
            title: localize('editShortcut.dialog.title'),
            text: localize('areYouSureReassign.msg', {
              key: getKeyLabel(key, localize),
              current: action.description,
              action: newAction.description
            }),
            firstAction: {
              text: localize('reassign.button.label'),
              onPress: () => {
                setMessageDialogProps(prev => ({
                  ...prev,
                  isOpen: false
                }));
                action[platform] = keys.slice();
                action[platform][k] = '';
                newAction[platform][keyIndex] = key;
                setListData(listData.slice());
              }
            },
            dismissAction: {
              text: CANCEL,
              isDefault: true,
              onPress: () => setMessageDialogProps(prev => ({
                ...prev,
                isOpen: false
              }))
            }
          });
          return false;
        }
      }
    }
    return true;
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Dialog, {
      resizable: true,
      isOpen: props.isOpen,
      testId: props.testId,
      title: localize('manageDialog.dialog.title'),
      style: renderInlineCssVars({
        xs: dt.tokens['space-base-xs']
      }, 'keyboard-shortcuts-dialog-spacing'),
      size: {
        minHeight: '20rem',
        height: '40rem',
        width: '41rem'
      },
      firstAction: {
        text: localize('save.button.label'),
        onPress: () => validate().then(() => props.onSave(saveListData(props.shortcuts, listData)), () => {})
      },
      dismissAction: {
        text: localize('cancel.button.label'),
        onPress: props.onCancel
      },
      subContentFooterControl: /*#__PURE__*/_jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/_jsx(Label, {
          helpPopup: {
            contentKey: 'keyboard.shortcutKeysNote',
            library: 'novacmneua-gui-eua'
          },
          children: localize('footNote.msg')
        })
      }),
      children: /*#__PURE__*/_jsxs(Flow, {
        className: cn.content,
        orientation: "vertical",
        children: [/*#__PURE__*/_jsx(Toolbar, {
          items: [{
            type: 'SearchField',
            value: searchValue,
            onChange: e => {
              listState.clearMeasurementCache({
                startRowIndex: 0,
                startColumnIndex: 0
              });
              setSearchValue(e.value);
            },
            filterMode: true,
            numberOfResults: matchCount
          }, {
            type: 'Select',
            values: platforms,
            selectedIndex: platforms.findIndex(item => item.key === platform),
            ariaLabel: localize('platform.aria'),
            onSelected: index => validate().then(() => setPlatform(platforms[index].key), () => {})
          }, {
            type: 'ToolbarSpacer'
          }, {
            type: 'Button',
            overflowPriority: 'always',
            text: localize('import.menuitem.label'),
            onPress: () => fileImporterRef.current?.open()
          }, {
            type: 'Button',
            overflowPriority: 'always',
            text: localize('export.menuitem.label'),
            onPress: () => download(JSON.stringify(saveListData(props.shortcuts, listData), undefined, 4), fileName)
          }, {
            type: 'Button',
            overflowPriority: 'always',
            text: localize('reset.menuitem.label'),
            onPress: () => {
              listData.forEach(action => {
                PLATFORMS.forEach(p => action[p] = undefined);
              });
              setListData(listData.slice());
            }
          }]
        }), /*#__PURE__*/_jsx("div", {
          className: cn.listContainer,
          children: /*#__PURE__*/_jsx("div", {
            ref: setRef,
            className: cn.listParent,
            children: /*#__PURE__*/_jsx(List, {
              ...listState,
              ariaLabel: localize('keyboardShortcuts.aria'),
              height: dimensions?.height,
              width: dimensions?.width,
              rowCount: filteredListData.length,
              itemHeight: () => 80,
              itemColumn: {
                key: '0',
                item: (index, ref) => {
                  const item = filteredListData[index];
                  if ('group' in item) return /*#__PURE__*/_jsx(GroupHeaderItem, {
                    title: item.group
                  });
                  const action = listData[item.originalIndex];
                  const defaults = action.default[platform];
                  const keys = action[platform] ?? defaults ?? [];
                  const actionLabel = action.noColonName ? action.description : localize('actionColon.label', {
                    action: action.description
                  });
                  return /*#__PURE__*/_jsxs("div", {
                    className: cn.grid,
                    children: [/*#__PURE__*/_jsx(Label, {
                      className: cn.label,
                      highlightableConfig: {
                        highlight: searchValue
                      },
                      wrapText: false,
                      required: action.required,
                      children: actionLabel
                    }), Array(SHORTCUT_COUNT).fill(0).map((_, k) => /*#__PURE__*/_jsx(ShortcutsInput, {
                      className: k === 0 ? cn.shortcut : cn.altShortcut,
                      value: keys[k] ?? '',
                      defaultValue: defaults?.[k] ?? '',
                      platform: platform,
                      ariaLabel: `${actionLabel} ${localize('shortcut.aria', {
                        number: (k + 1).toString()
                      })}`,
                      ref: k === 0 ? ref : undefined,
                      readOnly: k === 0 ? action.editable === false : undefined,
                      showError: k === 0 && action.required && keys.findIndex(value => value) === -1,
                      onChange: value => {
                        action[platform] = keys.slice();
                        if (!value || tryGrab(value, item.originalIndex, k)) {
                          action[platform][k] = value;
                          setListData(listData.slice());
                        }
                      }
                    }, k))]
                  });
                },
                containsInteractiveContent: true
              },
              narration: {
                onSayCell: params => {
                  const item = filteredListData[params.current.rowIndex];
                  if ('group' in item) return params.default;
                  const action = listData[item.originalIndex];
                  const defaults = action.default[platform];
                  const keys = action[platform] ?? defaults ?? [];
                  const actionLabel = action.noColonName ? action.description : localize('actionColon.label', {
                    action: action.description
                  });
                  const shortcutLabels = Array(SHORTCUT_COUNT).fill(0).map((_, k) => keys[k]).filter(item => item);
                  return `${actionLabel} ${shortcutLabels.join(', ')}`;
                }
              }
            })
          })
        }), /*#__PURE__*/_jsx(FileImporter, {
          fileType: fileExt,
          ref: fileImporterRef,
          onImport: onImport
        })]
      })
    }), messageDialogProps ? /*#__PURE__*/_jsx(MessageDialog, {
      ...messageDialogProps
    }) : null]
  });
};
function getListData(shortcuts, localize) {
  const listData = [];
  for (const i in shortcuts) {
    if (shortcuts.hasOwnProperty(i)) {
      const group = shortcuts[i];
      if (group.description.endsWith('.label')) group.description = localize(group.description);
      const actionList = group.actions;
      for (const j in actionList) {
        if (actionList.hasOwnProperty(j)) {
          const action = actionList[j];
          if (action.description.endsWith('.label')) action.description = localize(action.description);
          listData.push({
            group: group.description,
            noColonName: NO_COLON_NAME.includes(j),
            ...action
          });
        }
      }
    }
  }
  return listData;
}
function filterListData(listData, localize, searchValue, platform) {
  const filteredListData = [];
  const lowercaseValue = searchValue.toLowerCase();
  let lastGroup = '';
  let matchCount = 0;
  for (let originalIndex = 0; originalIndex < listData.length; originalIndex++) {
    const {
      group,
      ...action
    } = listData[originalIndex];
    const defaults = action.default[platform];
    const keys = action[platform] ?? defaults ?? [];
    const keysInfo = keys.map(key => getKeyLabel(key, localize, platform)).join('\n');
    if (!searchValue || action.description.toLowerCase().includes(lowercaseValue) || keysInfo.toLowerCase().includes(lowercaseValue)) {
      matchCount++;
      if (group !== lastGroup) {
        lastGroup = group;
        filteredListData.push({
          group
        });
      }
      filteredListData.push({
        originalIndex
      });
    }
  }
  return {
    filteredListData,
    matchCount
  };
}
function saveListData(shortcuts, listData) {
  let index = 0;
  const result = {};
  for (const i in shortcuts) {
    if (shortcuts.hasOwnProperty(i)) {
      const group = shortcuts[i];
      const actionList = group.actions;
      // need clone so that action's future change won't affect saved data
      result[i] = {
        ...group,
        actions: {}
      };
      for (const j in actionList) {
        if (actionList.hasOwnProperty(j)) {
          const action = {
            ...actionList[j]
          };
          const data = listData[index++];
          PLATFORMS.forEach(p => {
            action[p] = data[p];
          });
          result[i].actions[j] = action;
        }
      }
    }
  }
  return result;
}
//# sourceMappingURL=ManageDialog.js.map